import React, { useEffect } from 'react';
import Icons from '../../../../../../components/icons/Icons';
import Heading from '../../../../../../components/common-components/zoom/components/heading/Heading';
import { HEADING } from '../../../../../../components/common-components/zoom/components/heading/constants/constants';
import { DiagnosticReport } from './DiagnosticReport';
import { MedicationRequest } from './MedicationRequest';
import { CarePlan } from './CarePlan';
import { Procedure } from './Procedure';
import FamilyMemberHistory from './FamilyMemberHistory';
import ServiceRequest from './ServiceRequest';
import { Appointment } from './Appointment';
import { DocumentReference } from './DocumentReference';
import { DocumentReference as Binary } from './DocumentReference';
import Immunization from './Immunization';
import ImmunizationRecommendation from './ImmunizationRecommendation';
import Invoice from './Invoice';
import Label from '../../../../../../components/common-components/label/Label';
import { componentKey as PatientDashboardComponentKey } from '../../../../PatientDashboard/PatientDashboardSlice';
import { useDispatch, useSelector } from 'react-redux';
import { getPatientDetails } from '../../../PatientDashboardSaga';
import moment from 'moment/moment';

export const formatResourceType = (type) => {
  return type.replace(/([a-z])([A-Z])/g, '$1 $2');
};

export const formatDate = (dateString) => {
    if (!dateString) return null;
    const date = new Date(dateString);
    if (isNaN(date)) return dateString;
    const day = String(date.getDate()).padStart(2, '0');
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const year = date.getFullYear();
    return `${day}/${month}/${year}`;
};

export const FieldRow = ({ label, value }) => {
  if (!value) return null;
  return (
    <div className="my-1">
      <Label className="mr-1" fontSize={"sm"} fontWeight='semibold'>{label}: </Label>
      <span>{value}</span>
    </div>
  );
};

const FHIRBundleViewer = ({ title, bundle, close }) => {
  const {currentPatientId, patientData } = useSelector((state) => state[PatientDashboardComponentKey]);
  const patientId = bundle?.entry?.reduce((acc,entry)=>{
    if(entry.resourceType===Patient){
      acc =entry.resource.id 
    }
    return acc
  },"")
  const dispatch = useDispatch()
  const composition = bundle.entry?.find(
    (entry) => entry?.resource?.resourceType === 'Composition'
  )?.resource;
  const getResourceByReference = (referenceId) =>
    bundle.entry?.find((entry) => entry.fullUrl === referenceId)?.resource || null;

  const practitionerEntries = bundle.entry?.filter(
    (entry) => entry?.resource?.resourceType === 'Practitioner'
  ) || [];

  const practitionersMap = new Map();
  practitionerEntries.forEach((entry) => {
    const resource = entry.resource;
    let name =
      resource.name?.[0]?.text ||
      `${resource.name?.[0]?.given?.join(' ') || ''} ${resource.name?.[0]?.family || ''}`.trim();

    if (name && !practitionersMap.has(name)) {
      practitionersMap.set(name, resource);
    }
  });
  const practitioners = Array.from(practitionersMap.values());

  useEffect(()=>{
  dispatch(getPatientDetails({patientId:currentPatientId}))
  },[])

  return (
    <div className="fixed inset-0 z-50 flex justify-center items-center bg-gray-900 bg-opacity-75 transition-opacity duration-300">
        <div className="bg-white border border-gray-200 rounded w-[95%] md:w-[65%] h-[95%] overflow-hidden">
            <div className="px-6 py-4 border-b border-gray-200 flex justify-between items-center">
            <Heading type={HEADING.H1}className="text-2xl font-semibold">
                {formatResourceType(title)}
            </Heading>
            <div className="flex justify-end items-center-b pb-3 hover:cursor-pointer" onClick={close}>
                <Icons iconName={"closeIcon"}/>
            </div>
            </div>

            <div className="flex flex-col h-[95%] overflow-y-auto  met-scrollbar">
            <div className="border-r border-gray-200 p-4 flex flex-col gap-4">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                {bundle.entry ? (
                    bundle?.entry?.map((entry) => {
                        if(!entry.resource) return null;
                        const resource = entry.resource;
                        const resourceType = resource.resourceType;
                        if (resourceType === 'Patient') {
                            return <Patient resource={resource} key={`${resourceType}${resource.id}`} patientData={patientData} />;
                        }
                        if (resourceType === 'Organization') {
                            return <Organization resource={resource} key={`${resourceType}${resource.id}`} />;
                        }
                        if (resourceType === 'Encounter') {
                            return <Encounter resource={resource} key={resource.id} />;
                        }
                        if(resourceType === 'AllergyIntolerance'){
                          return <Allergy resource={resource} key={resource.id}/>
                        }
                        return null;
                        })
                ) : null}

                {practitioners && practitioners?.length > 0 && (
                    <div className="p-4 border border-gray-200 rounded">
                    <Heading type={HEADING.H2} className="text-xl font-medium mb-2">Practitioners</Heading>
                    {practitioners.map((practitioner, index) => {
                        const name =
                        practitioner.name?.[0]?.text ||
                        `${practitioner.name?.[0]?.given?.join(' ') || ''} ${practitioner.name?.[0]?.family || ''}`.trim() ||
                        'N/A';

                        return (
                        <div
                            key={index}
                            className="mb-1 p-1 px-2 border border-gray-200 rounded"
                        >
                            <FieldRow label="Full Name" value={name} />
                            {practitioner.telecom?.map((telecom, idx) => (
                            <FieldRow
                                key={idx}
                                label={`Contact ${idx + 1} (${telecom.system || 'Other'})`}
                                value={telecom.value}
                            />
                            ))}
                        </div>
                        );
                    })}
                    </div>
                )}
                </div>

                {composition && composition.section ? (
                    <div className='mb-6 p-4 border border-gray-200 rounded'>
                        <div className='border-b-2 border-gray-200 mb-4'>
                            {composition?.title && <Heading type={HEADING.H1}>{composition?.title}</Heading>}
                            <FieldRow label="Status" value={composition?.status} />
                            <FieldRow label="Visit Date" value={formatDate(composition?.date)} />
                        </div>
                        
                        {(Array.isArray(composition.section) ? composition.section : [composition.section]).map((section, sIndex) =>
                            section.entry?.map((entryRef, index) => {
                                const resource = getResourceByReference(entryRef.reference);
                                if (!resource) return null;
                                const ResourceComponent = resourceComponents[resource.resourceType] || null;

                                return ResourceComponent ? (
                                    <div key={`${resource.id}-${sIndex}-${index}`}>
                                      {resource.resourceType === "Observation" ?  
                                      <ResourceComponent resource={resource} bundle={bundle}  title={section?.title}/>
                                       : <ResourceComponent resource={resource} bundle={bundle} />
                                     }
                                    </div>
                                ) : null;
                            })
                        )}
                    </div>
                    
                ) : null}
            </div>
            </div>
        </div>
        </div>
  );
};

export const renderObservationResult = (obs) => {
  if (!obs)
    return null;

  const { status, code, valueQuantity, valueString, valueCodeableConcept, interpretation } = obs;
  const value = valueQuantity? `${valueQuantity.value} ${valueQuantity.unit}`:"";
  return (
    <div>
      <FieldRow label="Status" value={status} />
      {code?.text?<FieldRow label={code?.text} value={value} />:<FieldRow label={code?.coding[0]?.display} value={value} />}
      {valueString && <FieldRow label="Value" value={valueString} />}
      {valueCodeableConcept && <FieldRow label="Value" value={valueCodeableConcept.text} />}
      {interpretation && <FieldRow label="Interpretation" value={interpretation?.text} />}
    </div>
  );
};

const Practitioner = ({ resource }) => (
  <div className="mb-6 p-4 border border-gray-200 rounded">
    <Heading type={HEADING.H3} className="text-lg font-semibold mb-2">
      {resource.resourceType}
    </Heading>
    <FieldRow label="Name" value={resource.name?.[0]?.text} />
    {resource.telecom?.map((telecom, index) => (
      <FieldRow key={index} label={`Contact ${index + 1}`} value={telecom.value} />
    ))}
  </div>
);

const Patient = ({ resource , patientData}) => (

  <div className="p-4 border border-gray-200 rounded">

    <Heading type={HEADING.H3} className="text-lg font-semibold mb-2">
      {resource.resourceType}
    </Heading>
    <FieldRow label="Name" value={resource?.name?.[0]?.text} />
    <FieldRow label="Patient Id" value={`METSL${patientData?.uuid?.slice(-4)}`} />
    <FieldRow label="Gender" value={resource?.gender} />
    <FieldRow label="Birthdate" value={formatDate(resource.birthDate)} />
    <FieldRow label="Address" value={resource?.address?.[0]?.text || patientData?.Address?.[0]?.addressLine1
} />
    <FieldRow label="Phone" value={resource?.telecom?.[0]?.value || patientData?.mobileNumber} />
    <FieldRow label="ABHA Address" value={patientData?.abhaAddress} />
    <FieldRow label="ABHA Number" value={patientData?.abhaNumber} />
  </div>
);

const Organization = ({ resource }) => (
  <div className="p-4 border border-gray-200 rounded">
    <Heading type={HEADING.H3} className="text-lg font-semibold mb-2">
      {resource?.resourceType}
    </Heading>
    <FieldRow label="Name" value={resource.name} />
    {resource.telecom?.map((telecom, index) => (
      <FieldRow key={index} label={`Contact ${index + 1}`} value={telecom.value} />
    ))}
    {resource?.address && <FieldRow label="Address" value={resource?.address[0]?.text} />}
    
  </div>
);

const Encounter = ({ resource }) => {
    const start = formatDate(resource.period?.start);
    const end = formatDate(resource.period?.end);
    
    const period = start && end ? `${start} to ${end}` : null;
    return (
        <div className="p-4 border border-gray-200 rounded">
            <Heading type={HEADING.H3} className="text-lg font-semibold mb-2">
                {resource.resourceType}
            </Heading>
            <FieldRow label="Status" value={resource.status} />
            <FieldRow label="Classification" value={resource.class?.display} />
            {/* <FieldRow label="Type" value={resource?.resourceType} />
            <FieldRow label="Priority" value={resource?.priority?.coding[0]?.display} />
            <FieldRow
                label="Period"
                value={period}
            />
            <FieldRow label="Service type" value={resource?.serviceType?.text || resource.serviceType?.coding[0]?.display} /> */}
        </div>
    )
};

const Observation = ({resource, bundle, title}) => {
    const { status, code, valueQuantity, valueString, valueCodeableConcept, interpretation, hasMember } = resource;
    const value = valueQuantity? `${valueQuantity.value} ${valueQuantity.unit}`:"";

    return(
        <div className="mb-6 p-4 bg-white rounded-lg shadow-md border border-gray-300">
            <Heading type={HEADING.H3} className="text-xl font-bold text-gray-800 mb-4">
                {formatResourceType(resource.resourceType)}
            </Heading>
            <div className="p-4 border border-gray-200 rounded">
                <FieldRow label="Status" value={status} />
                {code?.text?<FieldRow label={code?.text} value={value} />:<FieldRow label={code?.coding[0]?.display} value={value} />}
                {valueString && <FieldRow label="Value" value={valueString} />}
                {valueCodeableConcept && <FieldRow label="Value" value={valueCodeableConcept.text} />}
                {interpretation && <FieldRow label="Interpretation" value={interpretation?.text} />}
                {title && <FieldRow label={title} value={code?.text} />}
            </div>
            {hasMember?.length > 0 &&
                <div className="mt-4 pl-4 border-l border-gray-300">
                <Heading type={HEADING.H4}className="text-md font-medium text-gray-600 mb-2">Other observations</Heading>
                <div className="flex gap-2">
                {hasMember.map((res, index) => {
                  const getResourceByReference = (referenceId) =>
                    bundle.entry?.find((entry) => entry.fullUrl === referenceId)?.resource || null;
                  const resource = getResourceByReference(res.reference);
                  return (
                    <div key={index} className="mb-2 p-2 border border-gray-200 rounded">
                        {renderObservationResult(resource)}
                    </div>
                  )})}
                </div>
            </div>
            }
        </div>
    )
}

const Condition = ({resource}) => {
    const {code} = resource;
    return (
        <div className="mb-6 p-4 bg-white rounded-lg shadow-md border border-gray-300">
            <Heading type={HEADING.H3} className="text-xl font-bold text-gray-800 mb-4">
                {formatResourceType(resource?.resourceType)}
            </Heading>
            <FieldRow label={"Condition"} value={code?.text}/>
        </div>
    )
}

const Allergy = ({resource})=>{
  const {category} = resource
  return (
    <div className="mb-6 p-4 border border-gray-200 rounded">
        <Heading type={HEADING.H3} className="text-xl font-bold text-gray-800 mb-4">
            {formatResourceType(resource?.resourceType)}
        </Heading>
        <FieldRow 
            label="Allergy" 
            value={
                resource?.code?.text 
                    ?? resource?.code?.coding?.[0]?.display 
                    ?? ""
            }
        />
        <FieldRow 
            label="Clinical Status" 
            value={
                resource?.clinicalStatus?.text 
                    ?? resource?.clinicalStatus?.coding?.[0]?.display 
                    ?? ""
            }
        />
        <FieldRow 
            label="Verification Status" 
            value={
                resource?.verificationStatus?.text 
                    ?? resource?.verificationStatus?.coding?.[0]?.display 
                    ?? ""
            }
        />
        <FieldRow 
            label="Recorded" 
            value={resource?.recordedDate ? moment(resource.recordedDate).format('DD MMM YYYY, hh:mm A'): ""} 
        />
    </div>
)
}
const resourceComponents = {
  DiagnosticReport,
  DocumentReference,
  Binary,
  MedicationRequest,
  Organization,
  Encounter,
  Patient,
  Practitioner,
  CarePlan,
  Procedure,
  FamilyMemberHistory,
  Observation,
  Condition,
  ServiceRequest,
  Appointment,
  Immunization,
  ImmunizationRecommendation,
  Invoice
};

export default FHIRBundleViewer;
